/**
 * Winhouse — Forgot Password page.
 * Used by students, tutors, staff, and admin. Never reveals whether an email
 * exists in the system.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Mail, ArrowLeft, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email: email.trim().toLowerCase() }, { withCredentials: true });
      setDone(true);
    } catch (err) {
      // The backend always returns 200 (or 500). We surface a soft success regardless
      // so we never reveal account existence — only blocking errors warn the user.
      if (err?.response?.status >= 500) {
        toast.error('Could not process your request. Please try again in a minute.');
      } else {
        setDone(true);
      }
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-6" data-testid="forgot-home-link">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Winhouse
        </Link>

        <div className="bg-white border border-gray-200 rounded-2xl p-7 shadow-sm">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-red-700 font-semibold mb-2">
            <Sparkles className="h-3.5 w-3.5" /> Winhouse Portal
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Forgot your password?</h1>
          <p className="text-sm text-gray-600 mt-2">
            Enter the email on your Winhouse account. We'll send you a reset link that's valid for 1 hour.
          </p>

          {done ? (
            <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-sm" data-testid="forgot-success">
              <div className="flex items-center gap-2 text-emerald-800 font-bold mb-1">
                <Mail className="h-4 w-4" /> Check your inbox
              </div>
              <p className="text-emerald-900/80 text-[13px] leading-relaxed">
                If <strong>{email}</strong> matches a Winhouse account, you'll receive a reset link shortly. The email is from <code className="text-[12px]">welcome@winhousetutors.academy</code>. The link expires in 1 hour and can only be used once.
              </p>
              <p className="text-[11px] text-emerald-900/70 mt-3">
                Didn't see it? Check your spam folder or try again in a few minutes.
              </p>
              <div className="flex gap-2 mt-4">
                <Link to="/portal/login" className="h-10 px-4 rounded-full bg-black text-white text-xs font-bold uppercase tracking-wider inline-flex items-center" data-testid="forgot-back-to-login">Back to login</Link>
                <button onClick={() => { setDone(false); setEmail(''); }} className="h-10 px-4 rounded-full border border-gray-200 text-xs font-bold uppercase tracking-wider" data-testid="forgot-try-again">Use a different email</button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1.5">Email</label>
                <input type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com" autoComplete="email"
                  className="w-full h-11 px-4 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-red-200"
                  data-testid="forgot-email" />
              </div>
              <button type="submit" disabled={submitting || !email.trim()}
                className="w-full h-11 rounded-full bg-red-700 hover:bg-red-800 text-white text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-40"
                data-testid="forgot-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send reset link
              </button>
              <div className="text-center text-[12px] text-gray-500 pt-2 border-t border-gray-100">
                Remembered it? <Link to="/portal/login" className="text-red-700 font-semibold hover:underline" data-testid="forgot-login-link">Sign in</Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
